import { Column, Table } from '@tanstack/react-table'
import { ListChecksIcon } from '@phosphor-icons/react'
import { TOOLBAR_CONTAINER } from 'app/components/CompactLabel'
import classNames from 'classnames'
import { compact, entries } from 'lodash'
import { useCallback, useState } from 'react'
import { Search as SearchIcon } from 'iconoir-react'
import { Button } from 'app/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from 'app/components/ui/sheet'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { EllipsisHorizontalIcon } from '@heroicons/react/20/solid'
import { ControlledSearch } from 'app/components/ui/controlled-search'
import Spinner from '../Spinner'
import { ExportAllRows } from 'app/components/ui/export-all-rows'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { TableFiltersView as TableFilters } from 'app/components/ui/table-filters/table-filters'
import TableColumnSelector from './TableColumnSelector'
import { TableColumnLinesToggle } from './TableColumnLinesToggle'
import { BulkActionBar } from './bulkActions/BulkActionBar'
import { useTableBulkSelection } from './bulkActions/useTableBulkSelection'
import type { BulkActionsConfig } from './bulkActions/types'
import { DataTable } from 'app/components/ui/data-table'
import { buildClearedFilterParams } from 'app/lib/clearFilterParams'
import { useHasActiveFilters } from 'app/components/ui/table-filters/use-has-active-filters'
import { useHandleSearchParams } from 'app/utils'
import { FilterableColumn, FilterObject } from 'app/filter.utils'

export type SimpleTableComponentProps<T> = {
  table: Table<T>
  tableWrapperRef: React.RefObject<HTMLDivElement | null>
  data: T[] | undefined
  columnVisibility: { [k: string]: boolean }
  defaultColumnVisibility: { [k: string]: boolean }
  defaultColumnOrder: string[]
  toggleSort: (column: Column<T, unknown>) => void
  sortOrder?: { columnId?: string; order?: `asc` | `desc` }
  onResetSizing: () => void
  onAutoFitColumn: (columnId: string, width: number) => void
  // Original props pass-through
  filterableColumns?: FilterableColumn[]
  showColumnOrdering?: boolean
  className?: string
  onSearchValueChange?: (searchValue: string) => void
  onScroll?: React.UIEventHandler<HTMLDivElement>
  wrapperClassName?: string
  extraSlot?: React.ReactElement
  extraRightSlot?: React.ReactElement
  hideFooter?: boolean
  enableExport?: boolean
  onExport?: () => Promise<void>
  onFiltersChange?: (filters: string) => void
  initialFilters?: FilterObject[]
  noDataMessage?: string
  emptyState?: React.ReactElement
  activeSubpage?: string
  enableHoverStateStyling?: boolean
  enableDeepLinking?: boolean
  edgePadded?: boolean
  bulkActions?: BulkActionsConfig<T>
  selectionMode?: boolean
  toggleSelectionMode?: () => void
  exitSelectionMode?: () => void
}

export function SimpleTableComponent<T>({
  table,
  tableWrapperRef,
  data,
  columnVisibility,
  defaultColumnVisibility,
  defaultColumnOrder,
  toggleSort,
  sortOrder,
  onResetSizing,
  onAutoFitColumn,
  filterableColumns,
  showColumnOrdering = false,
  className,
  onSearchValueChange,
  onScroll,
  wrapperClassName,
  extraSlot,
  extraRightSlot,
  hideFooter = false,
  enableExport = false,
  onExport,
  onFiltersChange,
  initialFilters,
  noDataMessage = `No data found`,
  emptyState,
  activeSubpage,
  enableHoverStateStyling = true,
  enableDeepLinking = true,
  edgePadded,
  bulkActions,
  selectionMode = false,
  toggleSelectionMode,
  exitSelectionMode,
}: SimpleTableComponentProps<T>) {
  // The shell takes ownership of the search text and the encoded filter string
  // so the empty state can tell "you have not created any of these yet" from
  // "nothing matched what you typed", and so Clear can reset both. The call
  // site still receives every change through its own callbacks.
  const [searchValue, setSearchValue] = useState(``)
  const isMobile = useIsMobile()
  const bulkSelection = useTableBulkSelection(table)
  const [moreOpen, setMoreOpen] = useState(false)
  const [filterString, setFilterString] = useState(``)
  const handleSearchParams = useHandleSearchParams()
  // useHasActiveFilters reads the live `filters` URL param, but that param is
  // only this table's own when it is actually deep linked. A table instance
  // with enableDeepLinking={false} (e.g. a drill-down modal on a page whose
  // main table IS deep linked) must not have its "narrowed" state driven by
  // some other table's leftover URL param, so the hook result is discarded
  // when this instance opted out. The hook itself can't be called
  // conditionally, so it's called unconditionally and gated here instead.
  const urlFiltersActive = useHasActiveFilters(filterableColumns, initialFilters)
  const hasUrlFilters = enableDeepLinking && urlFiltersActive
  const hasNarrowed = !!searchValue || !!filterString || hasUrlFilters

  const handleSearchValueChange = useCallback(
    (value: string) => {
      setSearchValue(value)
      onSearchValueChange?.(value)
    },
    [onSearchValueChange],
  )

  const handleFiltersChange = useCallback(
    (encoded: string) => {
      setFilterString(encoded)
      onFiltersChange?.(encoded)
    },
    [onFiltersChange],
  )

  // Mirrors clearFilters in use-table-filters.ts: this shell has no access to
  // that hook's internal `filters` state, so the secondary-attribute params
  // are derived from the declared columns instead of the currently-active
  // filter list.
  const clearAll = useCallback(() => {
    setSearchValue(``)
    setFilterString(``)
    onSearchValueChange?.(``)
    onFiltersChange?.(``)
    if (enableDeepLinking && filterableColumns?.length) {
      // Read the URL at click time rather than subscribing to it: this shell is
      // used inside modals and drawers that are not deep linked, and a
      // useSearchParams subscription here would re-render every one of them on
      // any unrelated param change.
      const currentParams = new URLSearchParams(window.location.search)
      handleSearchParams(buildClearedFilterParams({ columns: filterableColumns, searchParams: currentParams }))
    }
  }, [onSearchValueChange, onFiltersChange, enableDeepLinking, filterableColumns, handleSearchParams])

  // Export, row lines and the column picker. Inline from md up; on a phone they fold into the
  // same "More table options" sheet TableLayout uses, since four buttons beside Bulk actions
  // ran off a 375px screen.
  const utilityControls = (
    <>
      {enableExport ? (
        <ExportAllRows
          onExport={onExport}
          allRows={table.getRowModel().rows?.map((row) =>
            compact([
              Object.prototype.hasOwnProperty.call(row.original, `workspaceId`)
                ? { 'Workspace ID': row.original[`workspaceId`] }
                : undefined,
              Object.prototype.hasOwnProperty.call(row.original, `locationId`)
                ? { 'Location ID': row.original[`locationId`] }
                : undefined,
              ...row.getVisibleCells().map((cell) => ({
                [`${cell.column.columnDef.header}`]:
                  typeof cell.renderValue() === `number` && isNaN(cell.renderValue() as number)
                    ? ``
                    : cell.renderValue(),
              })),
            ]).reduce((r, c) => Object.assign(r, c), {}),
          )}
        />
      ) : null}
      {/* Same control as the main table toolbar: the row-lines preference is
      global, so any table showing controls offers the switch. Tables with
      no toolbar at all stay bare rather than growing one for this. */}
      <TableColumnLinesToggle />
      {showColumnOrdering && (
        <TableColumnSelector
          table={table}
          columnVisibility={columnVisibility}
          defaultColumnVisibility={defaultColumnVisibility}
          defaultColumnOrder={defaultColumnOrder}
          activeSubpage={activeSubpage}
          onResetSizing={onResetSizing}
        />
      )}
    </>
  )

  const showToolbar =
    !!onSearchValueChange || !!extraSlot || !!filterableColumns || enableExport || showColumnOrdering || !!bulkActions
  const hasLeftContent = !!onSearchValueChange || !!extraSlot || !!filterableColumns

  return (
    // `relative` only when there is a bar to dock: it is what the docked bar
    // positions against, and giving every other table a new positioning
    // context would move whatever they park inside themselves.
    <div
      data-table
      className={classNames(`flex h-full max-h-full flex-col overflow-hidden pb-3`, bulkActions && `relative`)}
    >
      {showToolbar && (
        <div
          className={classNames(
            // min-h and flex-wrap, not a fixed 40px line: when the left half cannot get its
            // 240px the right-hand controls drop to a line of their own, and when the left
            // half's own controls wrap the row grows instead of spilling above and below.
            `@container/toolbar-row sticky top-0 z-20 mb-6 flex min-h-[40px] flex-wrap justify-between gap-2 bg-surface`,
            edgePadded && `pr-1.5`,
          )}
        >
          {/* min-w-0 + flex-1 lets the search shrink so the right-hand controls
              (export, column selector) stay on-screen in a narrow modal; the
              search returns to its fixed 250px from md up. When there is no
              left content, hide this spacer on mobile so justify-between drops
              the right-hand controls to the left edge instead of the far right
              (it still grows at md to keep controls right, under any overlay
              title). */}
          <div
            className={
              hasLeftContent
                ? `${TOOLBAR_CONTAINER} flex min-w-0 flex-1 basis-60 items-center gap-2`
                : `${TOOLBAR_CONTAINER} hidden min-w-0 items-center gap-2 md:flex md:flex-1`
            }
          >
            {onSearchValueChange && (
              <ControlledSearch
                className="w-full min-w-0 flex-1 md:w-62.5 md:flex-none"
                value={searchValue}
                onValueChange={handleSearchValueChange}
              />
            )}
            {extraSlot ? extraSlot : null}
            {filterableColumns && (
              <TableFilters
                columns={filterableColumns}
                onFiltersChange={handleFiltersChange}
                enableDeepLinking={enableDeepLinking}
                initialFilters={initialFilters}
              />
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {bulkActions && (
              <Button
                variant={selectionMode ? `white` : `secondary`}
                size="sm"
                onClick={() => toggleSelectionMode?.()}
                title={selectionMode ? undefined : `Bulk actions`}
              >
                {selectionMode ? (
                  `Done`
                ) : (
                  <>
                    <ListChecksIcon size={16} />
                    <span className="@max-3xl/toolbar-row:hidden">Bulk actions</span>
                  </>
                )}
              </Button>
            )}
            {extraRightSlot ? extraRightSlot : null}
            {isMobile ? (
              <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
                <SheetTrigger asChild>
                  <Button variant="white" size="icon-sm" aria-label="More table options">
                    <EllipsisHorizontalIcon className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                {/* Auto-focusing the first control (Export) pops its focus-triggered tooltip on open. */}
                <SheetContent
                  side="bottom"
                  className="rounded-t-xl pb-[env(safe-area-inset-bottom)]"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <SheetHeader>
                    <SheetTitle>Table options</SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-col items-start gap-3 p-4">{utilityControls}</div>
                </SheetContent>
              </Sheet>
            ) : (
              utilityControls
            )}
          </div>
        </div>
      )}
      <div
        ref={tableWrapperRef as React.RefObject<HTMLDivElement | null>}
        className="flex flex-grow flex-col overflow-hidden"
      >
        {!data ? (
          <div className="my-5 grid w-full place-items-center">
            <Spinner size={10} />
          </div>
        ) : (
          <DataTable
            table={table}
            showFooter={!hideFooter && !!data.length}
            className={className}
            toggleSort={toggleSort}
            sortOrder={sortOrder}
            onScroll={onScroll}
            wrapperClassName={wrapperClassName}
            enableHoverStateStyling={enableHoverStateStyling}
            onAutoFitColumn={onAutoFitColumn}
            emptyState={
              hasNarrowed ? (
                <TableEmptyState
                  icon={<SearchIcon className="h-6 w-6" strokeWidth={1.5} />}
                  title="No matches"
                  description={
                    searchValue ? `Nothing matches "${searchValue}".` : `Nothing matches the current filters.`
                  }
                  action={
                    <Button variant="secondary" size="sm" onClick={clearAll}>
                      Clear search and filters
                    </Button>
                  }
                />
              ) : (
                (emptyState ?? <TableEmptyState title={noDataMessage} />)
              )
            }
          />
        )}
      </div>
      {bulkActions && selectionMode && (
        <BulkActionBar selection={bulkSelection} config={bulkActions} onExit={exitSelectionMode ?? (() => {})} />
      )}
    </div>
  )
}
